import { useState } from 'react';
import { api } from '../api.js';
import type { ÉtatSession } from '../api.js';

interface Props {
  onSession: (état: ÉtatSession) => void;
}

/**
 * Connexion en deux étapes : mot de passe, puis code à usage unique.
 *
 * Le découpage suit celui de l'API et évite de demander le code avant d'avoir vérifié le mot de
 * passe — inutile, et cela consommerait un code pour rien.
 */
export function Connexion({ onSession }: Props) {
  const [étape, setÉtape] = useState<'identifiants' | 'code'>('identifiants');
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [code, setCode] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function soumettreIdentifiants(événement: React.FormEvent) {
    événement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      const { status, data } = await api.connexion(email, motDePasse);
      if (status !== 200) {
        setErreur(data.error ?? 'Connexion refusée');
        return;
      }
      if (data.totpRequired === true) {
        setÉtape('code');
        return;
      }
      onSession(data);
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(false);
    }
  }

  async function soumettreCode(événement: React.FormEvent) {
    événement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      const { status, data } = await api.validerTotp(code);
      if (status !== 200) {
        setErreur(data.error ?? 'Code refusé');
        setCode('');
        return;
      }
      onSession(data);
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(false);
    }
  }

  return (
    <main className="coquille coquille--étroite">
      <header>
        <h1>Vigie</h1>
        <p className="sous-titre">Gestion du parc Ghost</p>
      </header>

      {étape === 'identifiants' ? (
        <form onSubmit={(e) => void soumettreIdentifiants(e)} className="carte">
          <h2>Connexion</h2>
          <label htmlFor="email">Adresse électronique</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <label htmlFor="motdepasse">Mot de passe</label>
          <input
            id="motdepasse"
            type="password"
            autoComplete="current-password"
            required
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
          />

          {erreur !== null && <p className="erreur">{erreur}</p>}
          <button type="submit" disabled={enCours}>
            {enCours ? 'Vérification…' : 'Se connecter'}
          </button>
        </form>
      ) : (
        <form onSubmit={(e) => void soumettreCode(e)} className="carte">
          <h2>Code de vérification</h2>
          <p className="aide">
            Saisis le code à six chiffres affiché par ton application d’authentification.
          </p>
          <label htmlFor="code">Code</label>
          <input
            id="code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            required
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
          {erreur !== null && <p className="erreur">{erreur}</p>}
          <button type="submit" disabled={enCours || code.length !== 6}>
            {enCours ? 'Vérification…' : 'Valider'}
          </button>
        </form>
      )}
    </main>
  );
}
